import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateSelectedBetOdds,
  updateSelectedBetStake,
  clearSelectedBet,
} from "../../store/slices/gameSlice";
import { toast } from "react-toastify";
import StakeSettingsModal from "./StakeSettingsModal";
import { isAuthenticated } from "../../utils/auth";
import {
  updateStakeSettingsAsync,
  getStakeSettingsAsync,
  getBalanceCRefAsync,
  makeFancyBetAsync,
  makeMarketBetAsync,
  fetchOpenBetsAsync,
  fetchUnmatchedBets,
} from "../../store/slices/betSlice";
import {
  fetchTeamPositionAsync,
  fetchFancyLiabilityAsync,
} from "../../store/slices/gameSlice";

const DEFAULT_STAKE_BUTTONS = [100, 200, 500, 1000, 2000, 5000, 10000, 25000];

const PlaceBetOpenBets = ({
  matchId,
  marketId,
  inline = false,
  showPlaceBet = true,
  showOpenBets = true,
  autoFetchOpenBets = false,
  filterToBookmarks = false,
  bookmarkedMarkets = [],
  bookmarkedFancies = [],
}) => {
  const [activeBetTab, setActiveBetTab] = useState("matched");
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [isPlacingBet, setIsPlacingBet] = useState(false);
  const [autoCloseSeconds, setAutoCloseSeconds] = useState(null);
  const autoCloseTimeoutRef = useRef(null);
  const autoCloseIntervalRef = useRef(null);
  const dispatch = useDispatch();
  const selectedBet = useSelector((state) => state.game.selectedBet);
  const stakeSettings = useSelector((state) => state.bet?.stakeSettings || []);
  const stakeSettingsLoaded = useSelector(
    (state) => state.bet?.stakeSettingsLoaded,
  );
  const balance = useSelector((state) => state.bet?.balance);
  const openBets = useSelector((state) => state.bet?.openBets);
  const unmatchedBetsRaw = useSelector((state) => state.bet?.unmatchedBets);

  useEffect(() => {
    if (!stakeSettingsLoaded) {
      dispatch(getStakeSettingsAsync());
    }
  }, [dispatch, stakeSettingsLoaded]);

  useEffect(() => {
    if (!autoFetchOpenBets) return;
    if (typeof window === "undefined") return;
    if (!isAuthenticated()) return;
    dispatch(fetchOpenBetsAsync());
  }, [autoFetchOpenBets, dispatch]);

  useEffect(() => {
    if (!matchId) return;
    if (typeof window === "undefined") return;
    if (!isAuthenticated()) return;
    dispatch(
      fetchUnmatchedBets({
        eventId: matchId,
        sportId: null,
      }),
    );
  }, [dispatch, matchId]);

  useEffect(() => {
    const clearTimers = () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
      if (autoCloseIntervalRef.current) {
        clearInterval(autoCloseIntervalRef.current);
        autoCloseIntervalRef.current = null;
      }
    };

    clearTimers();

    if (!selectedBet) {
      setAutoCloseSeconds(null);
      return clearTimers;
    }

    setAutoCloseSeconds(8);

    autoCloseIntervalRef.current = setInterval(() => {
      setAutoCloseSeconds((prev) => {
        if (typeof prev !== "number") return prev;
        return prev <= 1 ? 0 : prev - 1;
      });
    }, 1000);

    autoCloseTimeoutRef.current = setTimeout(() => {
      dispatch(clearSelectedBet());
    }, 8000);

    return clearTimers;
  }, [dispatch, selectedBet]);

  const allBets = React.useMemo(() => {
    if (!openBets) return [];

    // Shape 1: [{ metadata: [...], data: [...] }]
    if (Array.isArray(openBets)) {
      if (Array.isArray(openBets[0]?.data)) {
        return openBets[0].data;
      }
      return openBets;
    }

    // Shape 2: { data: [...] }
    if (Array.isArray(openBets.data)) return openBets.data;

    // Shape 3: { bets: [...] }
    if (Array.isArray(openBets.bets)) return openBets.bets;

    return [];
  }, [openBets]);

  const allUnmatchedBets = React.useMemo(() => {
    if (!unmatchedBetsRaw) return [];
    if (Array.isArray(unmatchedBetsRaw)) return unmatchedBetsRaw;
    if (Array.isArray(unmatchedBetsRaw?.data)) return unmatchedBetsRaw.data;
    return [];
  }, [unmatchedBetsRaw]);

  const bookmarkSets = React.useMemo(() => {
    const marketKeys = new Set();
    const fancyKeys = new Set();
    const matchIds = new Set();

    if (Array.isArray(bookmarkedMarkets)) {
      for (const b of bookmarkedMarkets) {
        const mId = b?.matchId != null ? String(b.matchId) : "";
        const mkId = b?.marketId != null ? String(b.marketId) : "";
        if (!mId || !mkId) continue;
        matchIds.add(mId);
        marketKeys.add(`${mId}::${mkId}`);
      }
    }

    if (Array.isArray(bookmarkedFancies)) {
      for (const b of bookmarkedFancies) {
        const mId = b?.matchId != null ? String(b.matchId) : "";
        const fId = b?.fancyId != null ? String(b.fancyId) : "";
        if (!mId || !fId) continue;
        matchIds.add(mId);
        fancyKeys.add(`${mId}::${fId}`);
      }
    }

    return { marketKeys, fancyKeys, matchIds };
  }, [bookmarkedMarkets, bookmarkedFancies]);

  const betsForCurrentMatch = React.useMemo(() => {
    if (!matchId) return allBets;

    const matchIdStr = String(matchId);
    const marketIdStr = marketId != null ? String(marketId) : null;

    return allBets.filter((bet) => {
      if (!bet) return false;
      const betMatchId =
        bet.match_id ?? bet.event_id ?? bet.matchId ?? bet.matchid ?? null;
      if (betMatchId == null || String(betMatchId) !== matchIdStr) {
        return false;
      }

      if (!marketIdStr) return true;

      const betMarketId = bet.market_id ?? bet.marketId ?? bet.marketid ?? null;
      return betMarketId != null && String(betMarketId) === marketIdStr;
    });
  }, [allBets, matchId, marketId]);

  const visibleBets = React.useMemo(() => {
    const base = betsForCurrentMatch;
    if (!filterToBookmarks) return base;

    const hasAnyBookmarks =
      bookmarkSets.marketKeys.size > 0 || bookmarkSets.fancyKeys.size > 0;
    if (!hasAnyBookmarks) return [];

    return base.filter((bet) => {
      if (!bet) return false;
      const betMatchId =
        bet.match_id ?? bet.event_id ?? bet.matchId ?? bet.matchid ?? null;
      const matchIdStr = betMatchId != null ? String(betMatchId) : "";
      if (!matchIdStr) return false;

      const isFancy = Number(bet?.is_fancy ?? 0) === 1;
      if (isFancy) {
        const betFancyId =
          bet.fancy_id ??
          bet.fancyId ??
          bet.selection_id ??
          bet.selectionId ??
          null;
        if (betFancyId == null) return false;
        return bookmarkSets.fancyKeys.has(
          `${matchIdStr}::${String(betFancyId)}`,
        );
      }

      const betMarketId = bet.market_id ?? bet.marketId ?? bet.marketid ?? null;
      if (betMarketId == null) return false;
      return bookmarkSets.marketKeys.has(
        `${matchIdStr}::${String(betMarketId)}`,
      );
    });
  }, [betsForCurrentMatch, filterToBookmarks, bookmarkSets]);

  const unmatchedBetsForCurrentMatch = React.useMemo(() => {
    if (!matchId) return allUnmatchedBets;

    const matchIdStr = String(matchId);
    return allUnmatchedBets.filter((bet) => {
      if (!bet) return false;
      const betMatchId =
        bet.match_id ?? bet.event_id ?? bet.matchId ?? bet.matchid ?? null;
      if (betMatchId == null) return false;
      return String(betMatchId) === matchIdStr;
    });
  }, [allUnmatchedBets, matchId]);

  const matchedBets = React.useMemo(
    () =>
      visibleBets.filter(
        (bet) =>
          Number(bet?.is_matched ?? 1) === 1 &&
          Number(bet?.is_fancy ?? 0) !== 1,
      ),
    [visibleBets],
  );

  const fancyBets = React.useMemo(
    () =>
      visibleBets.filter(
        (bet) =>
          Number(bet?.is_matched ?? 1) === 1 &&
          Number(bet?.is_fancy ?? 0) === 1,
      ),
    [visibleBets],
  );

  const stakeButtons =
    Array.isArray(stakeSettings) && stakeSettings.length
      ? stakeSettings
      : DEFAULT_STAKE_BUTTONS;

  const betSideClass =
    selectedBet && selectedBet.side === "lay" ? "lay" : "back";

  const isFancyBet = !!selectedBet?.isFancy;

  const currentOdds =
    typeof selectedBet?.odds === "number" && !Number.isNaN(selectedBet.odds)
      ? selectedBet.odds
      : 0;
  const currentStake =
    typeof selectedBet?.stake === "number" ||
    typeof selectedBet?.stake === "string"
      ? selectedBet.stake
      : "";

  const minStakeValue =
    typeof selectedBet?.minStake === "number" &&
    !Number.isNaN(selectedBet.minStake)
      ? selectedBet.minStake
      : 100;
  const maxStakeValue =
    typeof selectedBet?.maxStake === "number" &&
    !Number.isNaN(selectedBet.maxStake)
      ? selectedBet.maxStake
      : 25000;

  const stepOdds = (direction) => {
    if (!selectedBet) return;
    const step = 0.01;
    let next = currentOdds + (direction === "up" ? step : -step);
    if (next < 1.01) next = 1.01;
    if (next > 999.99) next = 999.99;
    // Round to 2 decimal places to avoid floating point artefacts
    next = Number(next.toFixed(2));
    dispatch(updateSelectedBetOdds(next));
  };

  const handleStakeInputChange = (event) => {
    dispatch(updateSelectedBetStake(event.target.value));
  };

  // For +100, +200 etc – add to existing stake
  const handleStakeIncrement = (amount) => {
    const current = Number(currentStake) || 0;
    const next = current + amount;
    if (next > maxStakeValue) {
      toast.error("Stake should be less than the max stake amount.");
      return;
    }
    dispatch(updateSelectedBetStake(String(next)));
  };

  // For Min Stake / Max Stake buttons – set absolute value
  const handleStakeSet = (amount) => {
    const target = Number(amount) || 0;
    if (target > maxStakeValue) {
      toast.error("Stake should be less than the max stake amount.");
      return;
    }
    dispatch(updateSelectedBetStake(String(target)));
  };

  const handleClearStake = () => {
    // Only clear the stake amount; keep the selected odds and selection
    dispatch(updateSelectedBetStake(""));
  };

  const handleCancelBet = () => {
    // Fully reset betslip to initial state
    dispatch(clearSelectedBet());
  };

  const handlePlaceBet = async () => {
    if (isPlacingBet) return;
    const rawStake = currentStake;
    if (
      rawStake === "" ||
      rawStake === null ||
      typeof rawStake === "undefined"
    ) {
      toast.error("Please enter a stake amount.");
      return;
    }

    const stakeNumber = Number(rawStake);
    if (!Number.isFinite(stakeNumber) || stakeNumber <= 0) {
      toast.error("Please enter a valid stake amount.");
      return;
    }

    if (stakeNumber < minStakeValue) {
      toast.error(
        "Stake should be greater than or equal to the minimum stake.",
      );
      return;
    }

    setIsPlacingBet(true);
    try {
      // 1) Fresh balance check
      const latestBalance = await dispatch(getBalanceCRefAsync()).unwrap();
      const available =
        typeof latestBalance === "object" && latestBalance !== null
          ? Number(latestBalance.balance)
          : Number(latestBalance);

      if (Number.isFinite(available) && stakeNumber > available) {
        toast.error("Insufficient balance to place bet.");
        return;
      }

      if (isFancyBet) {
        const fancyPayload = {
          is_back: selectedBet?.side === "back" ? 1 : 0,
          fancy_id: selectedBet?.fancyId,
          run: Number(selectedBet?.runs) || 0,
          size: Number(selectedBet?.size) || 0,
          stack: stakeNumber,
        };

        const result = await dispatch(makeFancyBetAsync(fancyPayload)).unwrap();

        if (result?.status) {
          toast.success(result?.msg || "Bet placed successfully");
        } else {
          toast.error(result?.msg || "Failed to place fancy bet");
        }
        // Refresh open bets after a successful fancy bet
        if (result?.status) {
          await dispatch(fetchOpenBetsAsync()).unwrap();
          if (matchId) {
            dispatch(fetchTeamPositionAsync(String(matchId)));
            dispatch(fetchFancyLiabilityAsync(String(matchId)));
          }
          dispatch(clearSelectedBet());
        }
      } else {
        const marketPayload = {
          is_back: selectedBet?.side === "back" ? 1 : 0,
          market_id: selectedBet?.marketId,
          odds: currentOdds,
          selection_id:
            selectedBet?.selectionId != null
              ? Number(selectedBet.selectionId)
              : null,
          stack: stakeNumber,
        };

        // eslint-disable-next-line no-console
        const result = await dispatch(
          makeMarketBetAsync(marketPayload),
        ).unwrap();
        // eslint-disable-next-line no-console

        if (result?.status) {
          toast.success(result?.msg || "Bet placed successfully");
        } else {
          toast.error(result?.msg || "Failed to place market bet");
        }
        // Refresh open bets after a successful market bet
        if (result?.status) {
          await dispatch(fetchOpenBetsAsync()).unwrap();
          if (matchId) {
            dispatch(fetchTeamPositionAsync(String(matchId)));
            dispatch(fetchFancyLiabilityAsync(String(matchId)));
          }
          dispatch(clearSelectedBet());
        }
      }
    } catch (error) {
      toast.error(
        error?.message || "Something went wrong while placing the bet.",
      );
    } finally {
      setIsPlacingBet(false);
    }
  };

  const handleOpenStakeSettings = () => {
    setShowStakeModal(true);
  };

  const handleCloseStakeSettings = () => {
    setShowStakeModal(false);
  };

  const handleSaveStakeSettings = async (values) => {
    if (!Array.isArray(values) || !values.length) {
      return;
    }

    try {
      await dispatch(
        updateStakeSettingsAsync({
          match_stack: values,
        }),
      ).unwrap();
      await dispatch(getStakeSettingsAsync()).unwrap();
      toast.success("Stake settings updated.");
      setShowStakeModal(false);
    } catch (error) {
      toast.error(
        error?.message || "Unable to update stake settings right now.",
      );
    }
  };

  if (inline && !selectedBet) {
    return null;
  }

  const containerClassName = inline
    ? "place-bet-inline"
    : "detail-right-sticky";

  return (
    <div className={containerClassName}>
      {showPlaceBet && (
        <div className="card mb-1 place-bet">
          <div className="card-header">
            <h6 className="card-header game-heading d-flex justify-content-between w-100">
              <span>
                Place Bet{" "}
                {/* {selectedBet && typeof autoCloseSeconds === "number" && (
                  <small className="ms-2">({autoCloseSeconds}s)</small>
                )} */}
              </span>
              {balance != null && (
                <span className="bet-balance ms-auto">
                  Balance:{" "}
                  <b>
                    {typeof balance === "object" && balance !== null
                      ? Number(balance.balance).toFixed(2)
                      : Number(balance).toFixed(2)}
                  </b>
                </span>
              )}
            </h6>
          </div>
          {selectedBet && (
            <div className={`place-bet-content black-bg-theme ${betSideClass}`}>
              <div id="goto-1.254698813-8586946" />
              <div className="table-responsive palce-tab-content">
                <form noValidate>
                  <table className="coupon-table table table-borderedless">
                    <tbody>
                      <tr className="middlesex-col">
                        <td className="bet-odds">
                          <div className="form-group">
                            <div className="input-group">
                              <button
                                type="button"
                                className="input-group-text odds-step-btn odds-step-btn-minus"
                                onClick={() => stepOdds("down")}
                                disabled={isFancyBet}
                              >
                                <i className="bi bi-dash" />
                              </button>
                              <input
                                placeholder="0"
                                type="text"
                                required
                                name=""
                                min="1.01"
                                max="999.99"
                                readOnly
                                value={
                                  isFancyBet
                                    ? selectedBet?.runs ||
                                      selectedBet?.selectionName ||
                                      ""
                                    : currentOdds || 0
                                }
                                className="ng-untouched ng-pristine ng-valid"
                              />
                              <button
                                type="button"
                                className="input-group-text odds-step-btn odds-step-btn-plus"
                                onClick={() => stepOdds("up")}
                                disabled={isFancyBet}
                              >
                                <i className="bi bi-plus" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="bet-stakes">
                          <div className="form-group">
                            <input
                              placeholder="0"
                              required
                              type="number"
                              min="0"
                              value={currentStake}
                              onChange={handleStakeInputChange}
                              className="ng-untouched ng-pristine ng-valid"
                            />
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="5" className="value-buttons">
                          {stakeButtons.map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              className="btn btn-secondary m-l-5 m-b-5"
                              onClick={() => handleStakeIncrement(amount)}
                            >
                              <i className="bi bi-plus" /> {amount}{" "}
                            </button>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="5" className="value-buttons">
                          <button
                            type="button"
                            className="btn btn-secondary m-l-5 m-b-5 btn-min-stake"
                            onClick={() => handleStakeSet(minStakeValue)}
                          >
                            min stake
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary m-l-5 m-b-5 btn-max-stake"
                            onClick={() => handleStakeSet(maxStakeValue)}
                          >
                            max stake
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary m-l-5 m-b-5 all-in-stake"
                            onClick={handleOpenStakeSettings}
                          >
                            Edit Stake
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary m-l-5 m-b-5 clear-stake"
                            onClick={handleClearStake}
                          >
                            clear
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="5">
                          <p className="bet-stakes">
                            Min Bet: {minStakeValue.toFixed(2)} Max Bet:{" "}
                            {maxStakeValue.toFixed(2)}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="col-md-12 px-0 text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleCancelBet}
                    >
                      {" "}
                      cancel{" "}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={handlePlaceBet}
                      disabled={isPlacingBet}
                    >
                      {" "}
                      {isPlacingBet ? "placing..." : "place bet"}{" "}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      {isPlacingBet && (
        <div className="text-center py-2">
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
          />
          Placing bet...
        </div>
      )}
      {showOpenBets && (
        <div className="card mb-1 place-bet">
          <div className="card-header">
            <h6 className="card-header game-heading">Open Bets</h6>
          </div>
          <div className="card-body p-0">
            <div id="OpenBets">
              <select className="form-control mobile-hide" />
              <div className="openBetsTabs accounts">
                <div className="tab-container">
                  <ul
                    role="tablist"
                    className="nav nav-tab nav-tabs-bordered"
                    aria-label="Tabs"
                  >
                    <li className="nav-item">
                      <button
                        type="button"
                        role="tab"
                        id="openbets-matched-tab"
                        className={`nav-link${
                          activeBetTab === "matched" ? " active" : ""
                        }`}
                        aria-controls="openbets-matched"
                        aria-selected={activeBetTab === "matched"}
                        onClick={() => setActiveBetTab("matched")}
                      >
                        <span>Matched</span>
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        role="tab"
                        id="openbets-unmatched-tab"
                        className={`nav-link${
                          activeBetTab === "unmatched" ? " active" : ""
                        }`}
                        aria-controls="openbets-unmatched"
                        aria-selected={activeBetTab === "unmatched"}
                        onClick={() => setActiveBetTab("unmatched")}
                      >
                        <span>UnMatched</span>
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        role="tab"
                        id="openbets-fancy-tab"
                        className={`nav-link${
                          activeBetTab === "fancy" ? " active" : ""
                        }`}
                        aria-controls="openbets-fancy"
                        aria-selected={activeBetTab === "fancy"}
                        onClick={() => setActiveBetTab("fancy")}
                      >
                        <span>Fancy</span>
                      </button>
                    </li>
                  </ul>
                  <div className="tab-content">
                    <div
                      id="openbets-matched"
                      role="tabpanel"
                      aria-labelledby="openbets-matched-tab"
                      className={`tab-pane${
                        activeBetTab === "matched" ? " active" : ""
                      }`}
                    >
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <td>Market</td>
                              <td>Sel Name</td>
                              <td>Odds</td>
                              <td>Stake</td>
                              <td>Date/Time</td>
                            </tr>
                          </thead>
                          <tbody>
                            {matchedBets.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  No matched bets
                                </td>
                              </tr>
                            )}
                            {matchedBets.map((bet, index) => {
                              const marketLabel =
                                bet?.market_name || bet?.match_name || "-";
                              const selName =
                                bet?.selectionName ||
                                bet?.selection_name ||
                                "-";
                              const oddsVal =
                                bet?.odds != null
                                  ? bet.odds
                                  : bet?.price != null
                                    ? bet.price
                                    : "-";
                              const stakeVal =
                                bet?.stake != null
                                  ? bet.stake
                                  : bet?.stack != null
                                    ? bet.stack
                                    : "-";
                              const createdRaw =
                                bet?.createdAt ||
                                bet?.created_at ||
                                bet?.bet_time ||
                                bet?.date ||
                                "";
                              const timeVal = createdRaw
                                ? new Date(createdRaw).toLocaleString([], {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "";
                              const isBack =
                                typeof bet?.is_back === "number"
                                  ? bet.is_back === 1
                                  : !!bet?.is_back;

                              const oddsClass = isBack ? "back" : "lay";

                              return (
                                <tr
                                  key={
                                    bet?.bet_id || bet?.id || bet?._id || index
                                  }
                                  className={oddsClass}
                                >
                                  <td>{marketLabel}</td>
                                  <td>{selName}</td>
                                  <td>{oddsVal}</td>
                                  <td>{stakeVal}</td>
                                  <td>{timeVal}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      id="openbets-unmatched"
                      role="tabpanel"
                      aria-labelledby="openbets-unmatched-tab"
                      className={`tab-pane${
                        activeBetTab === "unmatched" ? " active" : ""
                      }`}
                    >
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <td>Market</td>
                              <td>Odds</td>
                              <td>Stake</td>
                              <td>Date/Time</td>
                            </tr>
                          </thead>
                          <tbody>
                            {unmatchedBetsForCurrentMatch.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  No unmatched bets
                                </td>
                              </tr>
                            )}
                            {unmatchedBetsForCurrentMatch.map((bet, index) => {
                              const marketLabel =
                                bet?.market_name || bet?.match_name || "-";
                              const oddsVal =
                                bet?.odds != null
                                  ? bet.odds
                                  : bet?.price != null
                                    ? bet.price
                                    : "-";
                              const stakeVal =
                                bet?.stake != null
                                  ? bet.stake
                                  : bet?.stack != null
                                    ? bet.stack
                                    : "-";
                              const createdRaw =
                                bet?.createdAt ||
                                bet?.created_at ||
                                bet?.bet_time ||
                                bet?.date ||
                                "";
                              const timeVal = createdRaw
                                ? new Date(createdRaw).toLocaleString([], {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "";

                              return (
                                <tr
                                  key={
                                    bet?.bet_id || bet?.id || bet?._id || index
                                  }
                                >
                                  <td>{marketLabel}</td>
                                  <td>{oddsVal}</td>
                                  <td>{stakeVal}</td>
                                  <td>{timeVal}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      id="openbets-fancy"
                      role="tabpanel"
                      aria-labelledby="openbets-fancy-tab"
                      className={`tab-pane${
                        activeBetTab === "fancy" ? " active" : ""
                      }`}
                    >
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <td>Market</td>
                              <td>Sel Name</td>
                              <td>Odds</td>
                              <td>Stake</td>
                              <td>Date/Time</td>
                            </tr>
                          </thead>
                          <tbody>
                            {fancyBets.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  No fancy bets
                                </td>
                              </tr>
                            )}
                            {fancyBets.map((bet, index) => {
                              const marketLabel =
                                bet?.market_name ||
                                bet?.match_name ||
                                bet?.selectionName ||
                                bet?.selection_name ||
                                "-";
                              const selName =
                                bet?.selectionName ||
                                bet?.selection_name ||
                                "-";
                              const oddsVal =
                                bet?.odds != null
                                  ? bet.odds
                                  : bet?.price != null
                                    ? bet.price
                                    : "-";
                              const stakeVal =
                                bet?.stake != null
                                  ? bet.stake
                                  : bet?.stack != null
                                    ? bet.stack
                                    : "-";
                              const createdRaw =
                                bet?.createdAt ||
                                bet?.created_at ||
                                bet?.bet_time ||
                                bet?.date ||
                                "";
                              const timeVal = createdRaw
                                ? new Date(createdRaw).toLocaleString([], {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "";
                              const isBack =
                                typeof bet?.is_back === "number"
                                  ? bet.is_back === 1
                                  : !!bet?.is_back;
                              const oddsClass = isBack ? "back" : "lay";

                              return (
                                <tr
                                  key={
                                    bet?.bet_id || bet?.id || bet?._id || index
                                  }
                                  className={oddsClass}
                                >
                                  <td>{marketLabel}</td>
                                  <td>{selName}</td>
                                  <td>{oddsVal}</td>
                                  <td>{stakeVal}</td>
                                  <td>{timeVal}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <StakeSettingsModal
        show={showStakeModal}
        values={stakeButtons}
        onClose={handleCloseStakeSettings}
        onSave={handleSaveStakeSettings}
      />
    </div>
  );
};

export default PlaceBetOpenBets;
